/**
 * Drop-off belongings: how they go home (T-099). Pure rules behind the admin
 * Drop-off block's "Personal belongings" control. The partner portal
 * (aura-app dropoffLedgerModel) shows the same facts; keep the two in step.
 */
import { BelongingsContact, Client } from '../types';

export type BelongingsMethod = 'mail' | 'pickup';

export const BELONGINGS_METHOD_LABEL: Record<BelongingsMethod, string> = {
  mail: 'Mailed',
  pickup: 'Picked up',
};

/** Envelope / small bag defaults, matching the nestjs BELONGINGS_PARCEL. */
export const BELONGINGS_PARCEL = { weight: 0.3, length: 30, width: 23, height: 3 };

/** Snapshot of a case client's name, phone and address. */
export function contactFromClient(c: Partial<Client> & { id?: unknown }): BelongingsContact {
  const name =
    (c.name || [c.first_name, c.last_name].filter(Boolean).join(' ') || '').trim() || null;
  return {
    client_id: Number(c.id) || null,
    name,
    phone: (c.phone_number || '').trim() || null,
    civic_number: (c.civic_number || '').trim() || null,
    address: (c.address || '').trim() || null,
    apartment: ((c.apartment || c.address2 || '') as string).trim() || null,
    city: (c.city || '').trim() || null,
    province: (c.province || '').trim() || null,
    postal_code: (c.postal_code || '').trim().toUpperCase() || null,
  };
}

export function streetLine(c?: BelongingsContact | null): string {
  if (!c) return '';
  return [c.civic_number, c.address]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/** "12-345 Main St, Brampton, ON L6T 1A1". */
export function formatContactAddress(c?: BelongingsContact | null): string {
  if (!c) return '';
  const street = [c.apartment ? `${c.apartment.trim()}-` : '', streetLine(c)].join('');
  const cityLine = [c.city, [c.province, c.postal_code].filter(Boolean).join(' ')]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(', ');
  return [street, cityLine].filter(Boolean).join(', ');
}

/** What Canada Post (or the facility, for a pickup) still needs. */
export function missingContactParts(c?: BelongingsContact | null): string[] {
  if (!c) return ['name', 'street', 'city', 'province', 'postal code'];
  const missing: string[] = [];
  if (!c.name?.trim()) missing.push('name');
  if (!streetLine(c)) missing.push('street');
  if (!c.city?.trim()) missing.push('city');
  if (!c.province?.trim()) missing.push('province');
  if (!c.postal_code?.trim()) missing.push('postal code');
  return missing;
}

export type BelongingsLabelState = 'not_mail' | 'needs_address' | 'waiting' | 'ready';

/** Where the mail label stands, as staff see it. */
export function belongingsLabelState(f: {
  dropoff_belongings_method?: string | null;
  dropoff_belongings_contact?: BelongingsContact | null;
  dropoff_belongings_label_url?: string | null;
}): BelongingsLabelState {
  if (f.dropoff_belongings_method !== 'mail') return 'not_mail';
  if (f.dropoff_belongings_label_url) return 'ready';
  if (missingContactParts(f.dropoff_belongings_contact).length > 0) return 'needs_address';
  return 'waiting';
}

/** Same person + same address: changing to this needs no new label. */
export function sameContact(a?: BelongingsContact | null, b?: BelongingsContact | null): boolean {
  const norm = (c?: BelongingsContact | null) =>
    JSON.stringify(
      ['name', 'civic_number', 'address', 'apartment', 'city', 'province', 'postal_code'].map((k) =>
        String((c as any)?.[k] ?? '').trim().toLowerCase(),
      ),
    );
  return norm(a) === norm(b);
}
